import type { DeviceClass } from '@erectus/shared';
import { CAPTURE_DEFAULTS } from '@erectus/shared';

/**
 * Device classing drives the pose model, sample rate and the low-end guard rails
 * (docs/ARCHITECTURE.md §3: 2 GB-RAM devices are forced to lite + 2 fps).
 */
export function deviceClass(): DeviceClass {
  const mem = navigator.deviceMemory ?? 4;
  const cores = navigator.hardwareConcurrency ?? 4;
  if (mem <= 2 || cores <= 2) return 'low';
  if (mem <= 4 || cores <= 4) return 'mid';
  return 'high';
}

export function platform(): 'web' | 'ios' | 'android' {
  const anyWindow = window as unknown as { Capacitor?: { getPlatform(): string } };
  const p = anyWindow.Capacitor?.getPlatform();
  if (p === 'ios' || p === 'android') return p;
  return 'web';
}

export function isNative(): boolean {
  return platform() !== 'web';
}

/** Clamp the configured fps to what this device can plausibly sustain. */
/**
 * The sample rate: one value for every device and the whole session, for data
 * consistency (DATA_AND_ML §7). Only Remote Config moves it, and only within
 * the supported range. `cls` stays in the signature for callers; it no longer
 * lowers the rate.
 */
export function effectiveFps(configured: number, _cls: DeviceClass = deviceClass()): number {
  return Math.min(CAPTURE_DEFAULTS.maxSampleFps, Math.max(CAPTURE_DEFAULTS.minSampleFps, configured));
}

export function effectivePoseModel(
  configured: 'lite' | 'full',
  cls: DeviceClass = deviceClass(),
): 'lite' | 'full' {
  return cls === 'low' ? 'lite' : configured;
}
